import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/Field';
import { confirmPasswordReset } from '@/api/endpoints/auth';
import { errorMessage } from '@/api/errors';
import { AuthLayout } from './AuthLayout';

/** Matches the backend's MIN_PASSWORD_LENGTH; length only, no composition rules. */
const MIN_LENGTH = 8;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await confirmPasswordReset(token, password);
      toast.success('Password changed. Sign in with your new one.');
      // Deliberately not signed in automatically: an administrator still owes a
      // second factor, and possession of a mailbox is not possession of their phone.
      navigate('/login', { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="That link is incomplete"
        subtitle="Open the link from your email, or request a new one."
        footer={
          <Link to="/forgot-password" className="font-medium text-accent hover:text-accent-hover">
            Request a new link
          </Link>
        }
      >
        <p className="text-[13px] text-ink-secondary">
          The reset link carries a token that is missing from this address. Some mail
          clients split long links across lines — copying the whole thing usually fixes it.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      subtitle="This link works once."
      footer={
        <Link to="/login" className="font-medium text-accent hover:text-accent-hover">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          autoFocus
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint={`At least ${MIN_LENGTH} characters. Length beats punctuation.`}
        />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
        />

        {error && (
          <p className="rounded-[--radius-control] bg-bad-soft px-3 py-2 text-[13px] text-bad" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" variant="primary" loading={busy} className="w-full">
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}
